// hooks/usePickupPoints.ts
import Papa from 'papaparse';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FilterOption, PickupPointData, PickupPoints } from '../types/pickup-points';


export const usePickupPoints = () => {
  const [mergedPoints, setMergedPoints] = useState<PickupPoints[]>([]);
  const [filteredPoints, setFilteredPoints] = useState<PickupPoints[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOption, setFilterOption] = useState<FilterOption>('wholeCountry');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch and parse data with caching
  useEffect(() => {
    // const sundarbanCsvUrl = 'https://docs.google.com/spreadsheets/d/1Zfd9AieND9-vXms3K_0k9B-78WF42lLznkpRGaHGaxg/export?format=csv';
    const deliveryfastCsvUrl = 'https://docs.google.com/spreadsheets/d/10t96-dG9jo9Xns8o6l9tanTfaIGramUMNAb72bETREM/export?format=csv'
    const steadfastCsvUrl = 'https://docs.google.com/spreadsheets/d/1EQtRiUGGeUEDa_sdGT-zZlxa1CrzkmcpJE8pY5nsP1g/export?format=csv';

    const fetchData = async <T extends PickupPointData>(
      url: string,

      mapper: (point: T, index: number) => PickupPoints
    ) => {
      try {

        // Fetch and parse
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP error ${response.status}`);
        const csvData = await response.text();

        Papa.parse<T>(csvData, {
          header: true,
          complete: (result: { data: T[] }) => {
            const filteredData = result.data.filter(
              (point) =>
                point.DISTRICT_NAME && point.BRANCH_NAME && point.PICKUP_ADDRESS
            );
            const mappedData = filteredData.map(mapper);
            setMergedPoints((prev) => [...prev, ...mappedData]);
          },
          error: (err: Error) => {
            throw err;
          },
        });
      } catch (err) {
        console.error(`Error fetching:`, err);
        setError('Failed to load pickup points. Please try again later.');
      }
    };

    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        // fetchData<PickupPointData>(
        //   sundarbanCsvUrl,
        //   (point, index) => ({
        //     id: `sundarban-${index}`,
        //     districtEn: point.DISTRICT_NAME || '',
        //     branchEn: point.BRANCH_NAME || '',
        //     addressEn: point.PICKUP_ADDRESS || '',
        //     districtBn: point.DISTRICT_NAME_BN || '',
        //     branchBn: point.BRANCH_NAME_BN || '',
        //     addressBn: point.PICKUP_ADDRESS_BN || '',
        //     isInsideDhaka: point.IS_INSIDE_DHAKA === "1",
        //     provider: 'Sundarban',
        //     districtEnLower: (point.DISTRICT_NAME || '').toLowerCase(),
        //     branchEnLower: (point.BRANCH_NAME || '').toLowerCase(),
        //     addressEnLower: (point.PICKUP_ADDRESS || '').toLowerCase(),
        //     districtBnLower: (point.DISTRICT_NAME_BN || '').toLowerCase(),
        //     branchBnLower: (point.BRANCH_NAME_BN || '').toLowerCase(),
        //     addressBnLower: (point.PICKUP_ADDRESS_BN || '').toLowerCase(),
        //   })
        // ),
        fetchData<PickupPointData>(
          deliveryfastCsvUrl,
          (point, index) => ({
            id: `deliveryfast-${index}`,
            districtEn: point.DISTRICT_NAME || '',
            branchEn: point.BRANCH_NAME || '',
            addressEn: point.PICKUP_ADDRESS || '',
            districtBn: point.DISTRICT_NAME_BN || '',
            branchBn: point.BRANCH_NAME_BN || '',
            addressBn: point.PICKUP_ADDRESS_BN || '',
            isInsideDhaka: point.IS_INSIDE_DHAKA === "1",
            provider: 'Delivery Fast',
            districtEnLower: (point.DISTRICT_NAME || '').toLowerCase(),
            branchEnLower: (point.BRANCH_NAME || '').toLowerCase(),
            addressEnLower: (point.PICKUP_ADDRESS || '').toLowerCase(),
            districtBnLower: (point.DISTRICT_NAME_BN || '').toLowerCase(),
            branchBnLower: (point.BRANCH_NAME_BN || '').toLowerCase(),
            addressBnLower: (point.PICKUP_ADDRESS_BN || '').toLowerCase(),
          })
        ),
        fetchData<PickupPointData>(
          steadfastCsvUrl,
          (point, index) => ({
            id: `steadfast-${index}`,
            districtEn: point.DISTRICT_NAME || '',
            branchEn: point.BRANCH_NAME || '',
            addressEn: point.PICKUP_ADDRESS || '',
            districtBn: point.DISTRICT_NAME_BN || '',
            branchBn: point.BRANCH_NAME_BN || '',
            addressBn: point.PICKUP_ADDRESS_BN || '',
            isInsideDhaka: point.IS_INSIDE_DHAKA === "1",
            provider: 'Steadfast',
            districtEnLower: (point.DISTRICT_NAME || '').toLowerCase(),
            branchEnLower: (point.BRANCH_NAME || '').toLowerCase(),
            addressEnLower: (point.PICKUP_ADDRESS || '').toLowerCase(),
            districtBnLower: (point.DISTRICT_NAME_BN || '').toLowerCase(),
            branchBnLower: (point.BRANCH_NAME_BN || '').toLowerCase(),
            addressBnLower: (point.PICKUP_ADDRESS_BN || '').toLowerCase(),
          })
        ),
      ]);
      setLoading(false);
    };

    loadData();
  }, []);

  // Deduplicate and sort data
  useEffect(() => {
    if (!mergedPoints.length) return;
    if (!mergedPoints) return;
    // Sort by district and branch
    const sortedPoints = mergedPoints.sort((a, b) => {
      return a.districtEnLower.localeCompare(b.districtEnLower ?? '') || a.branchEnLower.localeCompare(b.branchEnLower);
    });

    setFilteredPoints(sortedPoints);
  }, [mergedPoints]);

  // Memoized filtering
  const filteredPointsMemo = useMemo(() => {
    let filtered = [...filteredPoints];

    if (filterOption === 'insideDhaka') {
      filtered = filtered.filter((point) => point.isInsideDhaka);
    } else if (filterOption === 'wholeCountry') {
      filtered = filtered.filter((point) => !point.isInsideDhaka);
    }

    if (searchTerm.trim() !== '') {
      const lowercasedTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (point) =>
          point.districtEnLower.includes(lowercasedTerm) ||
          point.branchEnLower.includes(lowercasedTerm) ||
          point.addressEnLower.includes(lowercasedTerm) ||
          point.districtBnLower.includes(lowercasedTerm) ||
          point.branchBnLower.includes(lowercasedTerm) ||
          point.addressBnLower.includes(lowercasedTerm)
      );
    }

    return filtered;
  }, [filteredPoints, filterOption, searchTerm]);

  const handleSearchChange = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  return {
    filteredPoints: filteredPointsMemo,
    loading,
    error,
    filterOption,
    setFilterOption,
    handleSearchChange,
  };
};